import React, { useState, useEffect, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';

interface CountdownConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  countdownSeconds?: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CountdownConfirmModal({
  isOpen,
  title,
  message,
  countdownSeconds = 3,
  onConfirm,
  onCancel,
}: CountdownConfirmModalProps) {
  const [remaining, setRemaining] = useState(countdownSeconds);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setRemaining(countdownSeconds);
      setCancelled(false);
      return;
    }

    setRemaining(countdownSeconds);
    setCancelled(false);

    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, countdownSeconds]);

  // Auto-submit when countdown reaches 0
  useEffect(() => {
    if (isOpen && remaining === 0 && !cancelled) {
      onConfirm();
    }
  }, [isOpen, remaining, cancelled, onConfirm]);

  const handleCancel = useCallback(() => {
    setCancelled(true);
    onCancel();
  }, [onCancel]);

  if (!isOpen) return null;

  const progress = ((countdownSeconds - remaining) / countdownSeconds) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100">
          <div
            className="h-full bg-green-500 transition-all duration-1000 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-6">
          {/* Icon and title */}
          <div className="text-center mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">{message}</p>
          </div>

          {/* Countdown */}
          <div className="text-center mb-5">
            <p className="text-sm text-gray-500">
              Submitting in <span className="font-bold text-green-600 text-lg">{remaining}</span> second{remaining !== 1 ? 's' : ''}...
            </p>
          </div>

          {/* Cancel button */}
          <button
            onClick={handleCancel}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-colors min-h-[48px]"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
